import React, { useState, useEffect } from 'react';
import PublicLayout from '../layouts/PublicLayout';
import { Shield, User, Lock, Eye, EyeOff } from 'lucide-react';
import CNESSLogo from '../components/CNESSLogo';

const LoginPage = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Only clear sessions if user explicitly navigates to login (not on page refresh)
  useEffect(() => {
    // Check if user was redirected here from a protected route or came directly
    const wasRedirected = sessionStorage.getItem('login_redirect');
    if (wasRedirected) {
      // User was redirected due to missing auth - don't clear, let them re-auth
      sessionStorage.removeItem('login_redirect');
      console.log('Login page: User redirected for re-authentication');
    }
    // Don't clear sessions automatically - only on explicit logout
  }, []);

  // SECURITY: No hardcoded credentials - all authentication via backend database only

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // First check backend database for user
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });

      if (response.ok) {
        const user = await response.json();
        
        // Store auth data persistently in localStorage (survives page refresh)
        localStorage.setItem('cness_authenticated', 'true');
        localStorage.setItem('cness_user_role', user.role);
        localStorage.setItem('cness_username', user.username);
        localStorage.setItem('user_id', user.id || user.user_id);
        localStorage.setItem('userData', JSON.stringify(user));
        localStorage.setItem('organization_id', user.organization_id || '');
        localStorage.setItem('auth_timestamp', Date.now().toString());
        
        // Also store in sessionStorage for backwards compatibility
        sessionStorage.setItem('cness_organization_id', user.organization_id || '');
        console.log('[LoginPage] Logged in as:', user.username, 'Role:', user.role, 'Org:', user.organization_id);
        
        // Redirect based on role
        if (user.role === 'super_admin' || user.role === 'system_admin') {
          window.location.replace('/system-admin');
        } else if (user.role === 'evaluator') {
          window.location.replace('/evaluator/dashboard');
        } else if (user.role === 'mentor') {
          window.location.replace('/mentor/dashboard');
        } else if (user.role === 'partner') {
          window.location.replace('/partner/dashboard');
        } else {
          window.location.replace('/platform');
        }
      } else {
        setError('Invalid username or password');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed. Please check your credentials and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  return (
    <PublicLayout>
      <>
        <section className="py-16 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white">
          <div className="container mx-auto px-6">
            <div className="max-w-md mx-auto">
              {/* Header */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center bg-white rounded-2xl px-5 py-3 mb-6 shadow-xl">
                  <CNESSLogo variant="full" />
                </div>
                <h1 className="text-3xl font-bold mb-2">Access CNESS Platform</h1>
                <p className="text-gray-300">Conscious Enterprise Management System</p>
              </div>

              {/* Login Form */}
              <div className="bg-white rounded-2xl shadow-2xl p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Username
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        name="username"
                        value={credentials.username}
                        onChange={handleInputChange}
                        autoComplete="username"
                        disabled={false}
                        readOnly={false}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                        placeholder="Enter your username"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={credentials.password}
                        onChange={handleInputChange}
                        autoComplete="current-password"
                        disabled={false}
                        readOnly={false}
                        className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
                        placeholder="Enter your password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium py-3 px-4 rounded-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Authenticating...
                      </div>
                    ) : (
                      'Access Platform'
                    )}
                  </button>
                </form>

                {/* Security Notice */}
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center">
                    <Shield className="h-5 w-5 text-blue-600 mr-3" />
                    <div>
                      <h3 className="text-sm font-medium text-blue-800">Secure Access</h3>
                      <p className="text-xs text-blue-700 mt-1">Please enter your authorized credentials to access the platform.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </>
    </PublicLayout>
  );
};

export default LoginPage;